import { ExtraParamsKey } from '@logto/schemas';

import { QA_APP_ID } from '@/constants/external-pages';
import {
  GTM_CONTAINER_IDS,
  GTM_SCRIPT_ID,
  GTM_SCRIPT_URL,
  PRODUCTION_AUTH_HOSTNAME,
  STAGING_AUTH_HOSTNAME,
} from '@/constants/tracking';
import { searchKeys } from '@/shared/utils/search-parameters';

import { initDataLayer } from './data-layer';

/**
 * NOTE: QA と STG は 1 つの Logto (STAGING_AUTH_HOSTNAME) を共用しているため、ホスト名だけでは
 * 見分けられず QA 用 RP の `app_id` で切り替える (#24 の QA_APP_ID と同じ判定)。
 */
export const resolveGtmId = (hostname: string, appId?: string): string | undefined => {
  if (hostname === PRODUCTION_AUTH_HOSTNAME) {
    return GTM_CONTAINER_IDS.prd;
  }

  if (hostname === STAGING_AUTH_HOSTNAME) {
    return appId === QA_APP_ID ? GTM_CONTAINER_IDS.qa : GTM_CONTAINER_IDS.stg;
  }

  return undefined;
};

/**
 * 値に個人情報・認可情報を含まないと分かっているクエリキー。
 *
 * NOTE: GTM (GA4 と広告タグ) は URL をそのまま読むため、これ以外のキー (OAuth の `code` / `state`、
 * `login_hint` のメールアドレス、`one_time_token` 等) やハッシュが URL にある間は GTM を読み込まない。
 * 未知のキーは安全側に倒して読み込まない。
 */
const trackableSearchKeys: ReadonlySet<string> = new Set([
  searchKeys.appId,
  searchKeys.organizationId,
  searchKeys.uiLocales,
  ExtraParamsKey.FirstScreen,
  ExtraParamsKey.InteractionMode,
  ExtraParamsKey.Identifier,
  ExtraParamsKey.DirectSignIn,
  // NOTE: use-fallback-route.ts の生リテラルと手動で同期している。
  'fallback',
]);

export const isTrackableLocation = (search: string, hash: string): boolean =>
  !hash && [...new URLSearchParams(search).keys()].every((key) => trackableSearchKeys.has(key));

export const loadGoogleTagManager = (gtmId: string) => {
  if (document.querySelector(`#${GTM_SCRIPT_ID}`)) {
    return;
  }

  initDataLayer();
  // eslint-disable-next-line @silverhand/fp/no-mutating-methods
  window.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  // eslint-disable-next-line @silverhand/fp/no-mutation
  script.id = GTM_SCRIPT_ID;
  // eslint-disable-next-line @silverhand/fp/no-mutation
  script.async = true;
  // eslint-disable-next-line @silverhand/fp/no-mutation
  script.src = `${GTM_SCRIPT_URL}?id=${encodeURIComponent(gtmId)}`;
  // NOTE: 読み込み失敗は data-layer.ts の sendEventAndWait がこの属性で判定する (待たずにリダイレクトするため)。
  script.addEventListener('error', () => {
    // eslint-disable-next-line @silverhand/fp/no-mutation
    script.dataset.failed = 'true';
  });
  document.head.append(script);
};
